import { useState, ReactNode } from "react";
import { DialogContent, DialogActions, Button, TextField, Typography, Divider } from "@mui/material";
import { useStore } from "../../../store/store";
import { useUser } from "../../../api/useAuth";

type WarningItem = {
    content: ReactNode;
    reasonPlaceholder: string;
    reasonLabel: string;
};

type MultiWarningDialogContentProps = {
    warnings: Array<WarningItem>;
};

const MIN_REASON_LENGTH = 10;

export const MultiWarningDialogContent = ({ warnings }: MultiWarningDialogContentProps) => {
    const { appDialog, reservedSlotWarning } = useStore();
    const { data: user } = useUser();
    const [reasons, setReasons] = useState<Array<string>>(() => warnings.map(() => ""));
    const [showErrors, setShowErrors] = useState(false);

    const isReasonInvalid = (reason: string) => reason.trim().length < MIN_REASON_LENGTH;

    const updateReason = (index: number, value: string) => {
        setReasons((current) => current.map((reason, reasonIndex) => (reasonIndex === index ? value : reason)));
        if (showErrors) setShowErrors(false);
    };

    const onConfirm = () => {
        if (reasons.some(isReasonInvalid)) {
            setShowErrors(true);
            return;
        }
        const userName = user ? `${user.first_name} ${user.last_name}` : "Unknown";
        const combinedReason = warnings
            .map((warning, index) => `${warning.reasonLabel}: ${reasons[index].trim()}`)
            .join(" | ");
        reservedSlotWarning.handleConfirm(`${userName} — ${combinedReason}`);
        appDialog.toggleIsOpen();
    };

    const onCancel = () => {
        reservedSlotWarning.handleCancel();
        appDialog.toggleIsOpen();
    };

    return (
        <>
            <DialogContent>
                {warnings.map((warning, index) => (
                    <div key={index}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {warning.content}
                        </Typography>
                        <TextField
                            fullWidth
                            label="Motivo"
                            placeholder={warning.reasonPlaceholder}
                            value={reasons[index]}
                            onChange={(event) => updateReason(index, event.target.value)}
                            error={showErrors && isReasonInvalid(reasons[index])}
                            helperText={
                                showErrors && isReasonInvalid(reasons[index])
                                    ? `Introduza pelo menos ${MIN_REASON_LENGTH} caracteres`
                                    : `${reasons[index].length}/${MIN_REASON_LENGTH}`
                            }
                            multiline
                            rows={2}
                            required
                        />
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={onConfirm} variant="contained" color="warning">
                    Agendar Mesmo Assim
                </Button>
            </DialogActions>
        </>
    );
};
